import { MeiliSearch, Config } from 'meilisearch';
import { generateTenantToken } from 'meilisearch/token';
import axios, { AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';
import { Headers as FetchHeaders } from 'cross-fetch';

export interface MeiliSearchConfig extends Omit<Config, 'httpClient'> {
  timeout?: number;
  retries?: number;
  retryDelayFactorMs?: number;
  maxRetryDelayMs?: number;
}

// exponentialDelay doubles this per attempt (~1s/2s/4s across 3 retries, plus 0-20% jitter),
// long enough to ride out brief Meilisearch Cloud blips instead of exhausting all retries
// within milliseconds. Retry-After response headers are honored when larger.
const DEFAULT_RETRY_DELAY_FACTOR_MS = 500;

// exponentialDelay takes max(backoff, Retry-After header), so a throttling response with a
// large Retry-After (Meilisearch Cloud commonly sends ~10s on 429) would otherwise stall
// synchronous callers whose retry budget is tuned around the backoff above.
const DEFAULT_MAX_RETRY_DELAY_MS = 5000;

class MeiliSearchClient extends MeiliSearch {
  private static normalizeHeaders(headers: HeadersInit | undefined): Record<string, string> | undefined {
    if (!headers) {
      return undefined;
    }

    const HeadersCtor: typeof Headers =
      typeof Headers !== 'undefined' ? Headers : (FetchHeaders as unknown as typeof Headers);
    const headersInstance = headers instanceof HeadersCtor ? headers : new HeadersCtor(headers);
    const normalizedHeaders: Record<string, string> = {};

    headersInstance.forEach((value, key) => {
      normalizedHeaders[key.toLowerCase()] = value;
    });

    return normalizedHeaders;
  }

  private static createAxiosInstance(config: MeiliSearchConfig): AxiosInstance {
    const axiosInstance = axios.create({
      timeout: config.timeout ?? 5000
    });

    axiosRetry(axiosInstance, {
      retries: config.retries ?? 3,
      retryDelay: (retryCount, error) =>
        Math.min(
          axiosRetry.exponentialDelay(retryCount, error, config.retryDelayFactorMs ?? DEFAULT_RETRY_DELAY_FACTOR_MS),
          config.maxRetryDelayMs ?? DEFAULT_MAX_RETRY_DELAY_MS
        ),
      retryCondition: (error) => {
        const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
        const isNetworkError = axiosRetry.isNetworkOrIdempotentRequestError(error);
        const is400Error = error.response?.status === 400;
        // Retry 400s due to transient auth connectivity issues in our MeiliSearch infrastructure
        // where the node doesn't have proper auth connectivity initially but establishes it on retry
        const is5xxError = (error.response?.status ?? 0) >= 500 && (error.response?.status ?? 0) < 600;
        // Meilisearch Cloud returns 408/429 while degraded or throttling; both are transient
        const isRetryable4xx = error.response?.status === 408 || error.response?.status === 429;
        const shouldRetry = isTimeout || isNetworkError || is400Error || is5xxError || isRetryable4xx;
        console.log('[MeiliSearchClient] Retry condition check:', {
          errorMessage: error.message,
          status: error.response?.status,
          isTimeout,
          isNetworkError,
          is400Error,
          is5xxError,
          isRetryable4xx,
          willRetry: shouldRetry
        });
        return shouldRetry;
      },
      onRetry: (retryCount, error, requestConfig) => {
        const message =
          typeof error === 'object' && error !== null && 'message' in error ? (error as any).message : String(error);
        console.warn(`[MeiliSearchClient] Retry attempt #${retryCount} for ${requestConfig?.url}: ${message}`, {
          body: requestConfig?.data
        });
      },
      shouldResetTimeout: true
    });

    return axiosInstance;
  }

  constructor(config: MeiliSearchConfig) {
    const axiosInstance = MeiliSearchClient.createAxiosInstance(config);

    super({
      ...config,
      httpClient: async (url, opts) => {
        const method = (opts?.method ?? 'GET').toUpperCase();
        // Log when the request is triggered
        console.log(`[MeiliSearchClient] ${method} request to ${url} triggered`, {
          body: opts?.body
        });
        try {
          const response = await axiosInstance.request({
            url: url.toString(),
            method,
            headers: opts?.headers ? MeiliSearchClient.normalizeHeaders(opts.headers) : undefined,
            data: opts?.body
          });
          // Log after request is done
          console.log(`[MeiliSearchClient] ${method} request to ${url} completed with status ${response.status}`);
          return response.data;
        } catch (error) {
          // Log the error but don't rethrow it yet to allow retry mechanism to work
          const errorMessage = error instanceof Error ? error.message : String(error);
          console.error(`[MeiliSearchClient] Error in ${method} request to ${url}: ${errorMessage}`);
          // Rethrow the error to allow retry mechanism to work
          throw error;
        }
      }
    });
  }
}

export * from 'meilisearch';
export { MeiliSearchClient as MeiliSearch };
export { generateTenantToken };

/**
 * Build a `field = "value"` Meilisearch filter clause with the value safely escaped.
 *
 * Interpolating an untrusted value straight into a filter string is a filter-injection hole:
 * a value like `foo" OR someField > 0` closes the quote and appends attacker-controlled boolean
 * logic, widening the result set past the intended row (see #35551 — unauthenticated cross-tenant
 * offer enumeration via the public getMostRecentOfferWithWebuserId resolver). Every call site that
 * filters on a caller-supplied value must go through this helper instead of a template literal so
 * the escaping can't be forgotten. Callers should ALSO validate correlation ids against an
 * allowlist before searching; this helper is the defense-in-depth backstop for anything they miss.
 *
 * Meilisearch treats `\` as an escape char inside a double-quoted string literal, so we escape
 * backslashes first (to avoid double-unescaping) and then the double quotes that would break out.
 * See https://www.meilisearch.com/docs/reference/api/search#filter.
 */
export function escapeFilterValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function equalsFilter(field: string, value: string): string {
  return `${field} = "${escapeFilterValue(value)}"`;
}

/**
 * Build a `field CONTAINS "value"` clause with the value safely escaped and quoted. Same
 * injection concern as {@link equalsFilter} — an unescaped (or, worse, unquoted) value lets a
 * caller inject filter syntax. Quoting the operand is intentional: Meilisearch accepts a quoted
 * string literal after CONTAINS, and quoting keeps a value with whitespace/operators inert.
 */
export function containsFilter(field: string, value: string): string {
  return `${field} CONTAINS "${escapeFilterValue(value)}"`;
}
